import { gql, type TypedDocumentNode } from '@apollo/client';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Navigate } from '@sorare/routing';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LegalWarningBanner } from '@sorare/core/src/components/jonum/LegalWarningBanner';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation';
import { ANY_SPORT_SERIES_TRAINING } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useShouldUseTraining } from '@sorare/core/src/hooks/commonGame/useIsInTraining.ts';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { SeriesDailyClaimModule } from '@sorare/marketplace/src/components/commonCard/BuyPacks/SeriesDailyClaimModule';
import { WelcomePackOrb } from '@sorare/marketplace/src/components/commonCard/BuyPacks/WelcomePackOrb/index.tsx';

import { ClaimStarterPack } from '#src/components/series/ClaimStarterPack/index.tsx';
import { DailyRewardsCalendar } from '#src/components/series/DailyRewardsCalendar/index.tsx';
import { SeriesBoardHeader } from '#src/components/series/SeriesBoard/SeriesBoardHeader/index.tsx';
import { SeriesBoard } from '#src/components/series/SeriesBoard/index.tsx';
import { SeriesMissions } from '#src/components/series/SeriesMissions/index.tsx';
import { SetInviteCodeDialog } from '#src/components/series/inviteFriends/SetInviteCodeDialog/index.tsx';

import type {
  SeriesPlayPageMissionsQuery,
  SeriesPlayPageMissionsQueryVariables,
  SeriesPlayPageQuery,
  SeriesPlayPageQueryVariables,
  UnclaimedDeliverableItemOrdersQuery,
  UnclaimedDeliverableItemOrdersQueryVariables,
} from './__generated__/page.graphql.ts';

const SERIES_PLAY_PAGE_QUERY = gql`
  query SeriesPlayPageQuery($sport: Sport!) {
    currentUser {
      slug
      availableVoucher(sport: $sport) {
        id
        ...WelcomePackOrb_availableVoucher
      }
      calendarTasksTrack(sport: $sport) {
        id
        ...DailyRewardsCalendar_taskInterface
      }
      setTasksTrack(sport: $sport) {
        id
        steps {
          id
          aasmState
          ... on ThresholdPickerTask {
            id
            averageMode
          }
        }
      }
      ...SeriesBoard_currentUser
      ...ClaimStarterPack_currentUser
    }
    cardSet {
      id
      replayFinished(sport: $sport)
      currentSet(sport: $sport) {
        slug
        ...SeriesDailyClaimModule_cardSet
        ...DailyRewardsCalendar_cardSet
      }
      nextSet(sport: $sport) {
        slug
        ...SeriesDailyClaimModule_cardSet
        ...DailyRewardsCalendar_cardSet
      }
      ...SeriesBoard_cardSetRoot
      ...ClaimStarterPack_cardSetRoot
    }
    market {
      id
      myCommonDailyClaimTask(sport: $sport) {
        id
        name
        ...SeriesDailyClaimModule_task
      }
    }
  }
  ${SeriesBoard.fragments.currentUser}
  ${SeriesDailyClaimModule.fragments.cardSet}
  ${SeriesDailyClaimModule.fragments.task}
  ${DailyRewardsCalendar.fragments.cardSet}
  ${DailyRewardsCalendar.fragments.taskInterface}
  ${WelcomePackOrb.fragments.availableVoucher}
  ${SeriesBoard.fragments.cardSet}
  ${ClaimStarterPack.fragments.currentUser}
  ${ClaimStarterPack.fragments.cardSetRoot}
` as TypedDocumentNode<SeriesPlayPageQuery, SeriesPlayPageQueryVariables>;

export const loader = queryLoader(SERIES_PLAY_PAGE_QUERY, ({ sport }) => ({
  sport,
}));

const SERIES_PLAY_PAGE_MISSIONS_QUERY = gql`
  query SeriesPlayPageMissionsQuery($sport: Sport!) {
    currentUser {
      slug
      ...SeriesMissions_currentUser
    }
    cardSet {
      id
      ...SeriesMissions_cardSetRoot
    }
  }
  ${SeriesMissions.fragments.currentUser}
  ${SeriesMissions.fragments.cardSet}
` as TypedDocumentNode<
  SeriesPlayPageMissionsQuery,
  SeriesPlayPageMissionsQueryVariables
>;

const Grid = styled.div`
  display: grid;
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height, 0px));
  @media ${desktopAndAbove} {
    grid-template-columns: auto 420px;
  }
`;

const BoardWrapper = styled.div`
  position: sticky;
  top: var(--current-stack-height, 0px);
  display: grid; /** forces the children to take the full available space */
  height: calc(var(--100vh) - var(--current-stack-height, 0px));
`;

const DesktopMissionsContainer = styled(Vertical).attrs({ gap: 5 })`
  position: relative;
  padding: var(--double-unit) var(--double-unit) var(--double-unit) 0;
`;

const BottomRightButtons = styled(Vertical)`
  position: absolute;
  bottom: 100px; /** offset the navigation bar */
  right: var(--double-unit);
  @media ${desktopAndAbove} {
    bottom: var(
      --quadruple-unit
    ); /** should be triple-unit but the dot make it visualy off */
    right: var(
      --quadruple-unit
    ); /** should be triple-unit but the dot make it visualy off */
  }
`;

const LegalWarningBannerStyled = styled(LegalWarningBanner)`
  background: var(--c-nd-50);
`;

const UNCLAIMED_DELIVERABLE_ITEM_ORDERS_QUERY = gql`
  query UnclaimedDeliverableItemOrdersQuery($sport: Sport!) {
    currentUser {
      slug
      mySetDeliverableItemOrders(sport: $sport) {
        id
        ...SeriesMissions_deliverableItemOrder
        ...SeriesBoard_deliverableItemOrder
      }
    }
  }
  ${SeriesMissions.fragments.deliverableItemOrder}
  ${SeriesBoard.fragments.deliverableItemOrder}
` as TypedDocumentNode<
  UnclaimedDeliverableItemOrdersQuery,
  UnclaimedDeliverableItemOrdersQueryVariables
>;

const SeriesPlayPage = () => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { sport, generateSportContextPath } = useSportContext();
  const { data, refetch } = useQueryLoader<SeriesPlayPageQuery>();
  const { data: missionsData } = useQuery(SERIES_PLAY_PAGE_MISSIONS_QUERY, {
    variables: { sport },
    skip: !isDesktopAndAbove,
  });
  const { currentUser: currentUserFromContext } = useCurrentUserContext();
  const { currentUser, market, cardSet } = data;
  const { setTasksTrack } = currentUser || {};
  const { steps } = setTasksTrack || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const dialog = searchParams.get('dialog');
  const showConnectTaskDialog = dialog === 'connect';
  const showInviteCodeDialog = dialog === 'invite-code';
  const closeDialog = () =>
    setSearchParams(
      prev => {
        prev.delete('dialog');
        return prev;
      },
      { replace: true }
    );
  const {
    flags: {
      useWelcomePromoPack = false,
      hideUnclaimedDeliverableItemOrders = true,
    },
  } = useFeatureFlags();
  const shouldUseTraining = useShouldUseTraining();

  const { data: unclaimedDeliverableItemOrdersData } = useQuery(
    UNCLAIMED_DELIVERABLE_ITEM_ORDERS_QUERY,
    {
      variables: {
        sport,
      },
      skip: hideUnclaimedDeliverableItemOrders,
    }
  );
  const unclaimedDeliverableItemOrder =
    unclaimedDeliverableItemOrdersData?.currentUser?.mySetDeliverableItemOrders?.at(
      0
    );

  if (shouldUseTraining) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_SERIES_TRAINING)}
        replace
        keepSearchParams
      />
    );
  }

  if (!currentUser) {
    return null;
  }

  const showClaimStarterPack =
    !currentUserFromContext?.anyUserSportProfile?.commonGameOnboarded ||
    !cardSet?.currentSet;

  if (showClaimStarterPack) {
    return (
      <ContentWithStickyHeader header={<SeriesBoardHeader />}>
        <ClaimStarterPack
          refetch={refetch}
          currentUser={currentUser}
          cardSetRoot={cardSet}
        />
      </ContentWithStickyHeader>
    );
  }

  if (!setTasksTrack) {
    return null;
  }

  const dailyClaimTask = isType(market.myCommonDailyClaimTask, 'Task')
    ? market.myCommonDailyClaimTask
    : undefined;

  const isAverageMode = steps?.some(
    step => isType(step, 'ThresholdPickerTask') && step.averageMode
  );
  const availableVoucher = currentUser?.availableVoucher;
  const { calendarTasksTrack } = currentUser;

  return (
    <>
      <Grid>
        <BoardWrapper>
          <SeriesBoard
            currentUser={currentUser}
            cardSet={cardSet}
            deliverableItemOrder={unclaimedDeliverableItemOrder}
            refetch={refetch}
            showConnectTaskDialog={showConnectTaskDialog}
            onConnectTaskDialogClose={() => {
              setSearchParams(
                prev => {
                  prev.delete('dialog');
                  return prev;
                },
                { replace: true }
              );
            }}
            isReplay={!!isAverageMode}
            onConnectTaskDialogOpen={() => {
              setSearchParams({ dialog: 'connect' }, { replace: true });
            }}
          />
          {!isAverageMode && (
            <BottomRightButtons>
              <DailyRewardsCalendar
                calendarTasksTrack={calendarTasksTrack}
                cardSet={cardSet?.currentSet ?? cardSet?.nextSet}
                refetch={refetch}
              />
              {!!(availableVoucher && useWelcomePromoPack) && (
                <WelcomePackOrb availableVoucher={availableVoucher} />
              )}
              {!!dailyClaimTask && (
                <SeriesDailyClaimModule
                  task={dailyClaimTask}
                  refetch={refetch}
                  cardSet={cardSet?.currentSet ?? cardSet?.nextSet}
                  onClaimed={() => {
                    refetch();
                  }}
                />
              )}
            </BottomRightButtons>
          )}
        </BoardWrapper>
        {!!isDesktopAndAbove && !!missionsData?.currentUser && (
          <DesktopMissionsContainer>
            <SeriesMissions
              currentUser={missionsData.currentUser}
              cardSet={missionsData.cardSet}
              deliverableItemOrder={unclaimedDeliverableItemOrder}
              showFeaturedCollectionTask={true}
            />
          </DesktopMissionsContainer>
        )}
        <AbsoluteNav />
      </Grid>
      <SetInviteCodeDialog
        open={showInviteCodeDialog}
        onClose={closeDialog}
        onCodeAdded={refetch}
      />
      <LegalWarningBannerStyled />
    </>
  );
};
export default SeriesPlayPage;
